import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, resolveImg, formatDate } from "@/lib/api";
import { toast } from "sonner";
import { Newspaper, Calendar, Mail, HelpCircle, DollarSign, ArrowRight } from "lucide-react";

/**
 * Article sidebar — a stack of click-through widgets. Each widget is a
 * self-contained card so writers/editors don't have to worry about layout.
 *
 * Widgets included:
 *   - Latest Titans News (excludes current article)
 *   - Next Game (schedule ticker)
 *   - Newsletter signup
 *   - Test Your Titans Fandom (trivia CTA)
 *   - Cap Central CTA
 */
export default function ArticleSidebar({ excludeArticleId }) {
  return (
    <aside className="space-y-5" data-testid="article-sidebar">
      <LatestNewsWidget excludeArticleId={excludeArticleId} />
      <NextGameWidget />
      <NewsletterWidget />
      <TriviaWidget />
      <CapCentralWidget />
    </aside>
  );
}

/* ---------------- WIDGETS ---------------- */

function WidgetShell({ children, icon: Icon, label, testid, accent = "blue" }) {
  const stripe = accent === "red"
    ? "bg-titans-red"
    : accent === "gold"
    ? "bg-[#F2A900]"
    : "bg-titans-blue";
  return (
    <div className={`titans-card p-5 relative overflow-hidden`} data-testid={testid}>
      <div className={`absolute top-0 left-0 h-1 w-16 ${stripe}`} />
      <div className="flex items-center gap-2 mb-3 pt-1">
        <Icon size={13} />
        <span className="font-cond uppercase tracking-[0.25em] text-[10px]">{label}</span>
      </div>
      {children}
    </div>
  );
}

function LatestNewsWidget({ excludeArticleId }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/news`, { params: { limit: 5 } });
        const arts = data?.articles || [];
        setItems(arts.filter((a) => a.id !== excludeArticleId).slice(0, 4));
      } catch { /* silent */ }
    })();
  }, [excludeArticleId]);

  return (
    <WidgetShell icon={Newspaper} label="Latest Titans News" testid="sidebar-latest-news">
      <ul className="divide-y divide-[rgba(15,23,42,0.08)]">
        {items.map((a, i) => (
          <li key={a.id} className="py-3 first:pt-0 last:pb-0">
            <Link
              to={`/news/${a.slug || a.id}`}
              state={{ article: a }}
              className="flex gap-3 group"
              data-testid={`sidebar-latest-news-${i}`}
            >
              <div className="w-16 h-14 shrink-0 overflow-hidden bg-titans-void">
                {a.image && (
                  <img
                    src={resolveImg(a.image)}
                    alt=""
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-cond uppercase tracking-[0.2em] text-[9px] text-titans-red mb-1">
                  {a.category || "Story"}
                </div>
                <div className="font-display uppercase text-sm leading-snug line-clamp-2 group-hover:text-titans-blue transition-colors">
                  {a.headline}
                </div>
              </div>
            </Link>
          </li>
        ))}
        {items.length === 0 && (
          <li className="text-titans-muted text-sm py-2">No stories yet.</li>
        )}
      </ul>
      <Link to="/news" className="mt-3 inline-flex items-center gap-1 font-cond uppercase tracking-wider text-xs text-titans-blue hover:text-titans-red">
        All news <ArrowRight size={12} />
      </Link>
    </WidgetShell>
  );
}

function NextGameWidget() {
  const [game, setGame] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/schedule`);
        const events = data?.events || [];
        // First uncompleted game
        const next = events.find((e) => !e.completed) || events[0];
        setGame(next || null);
      } catch { /* silent */ }
    })();
  }, []);

  if (!game) return null;

  return (
    <WidgetShell icon={Calendar} label="Upcoming Schedule" testid="sidebar-next-game" accent="red">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-cond uppercase tracking-[0.2em] text-[9px] text-titans-muted">Week</div>
          <div className="font-display text-2xl uppercase">{game.week ? `W${game.week}` : "—"}</div>
        </div>
        <div className="text-right">
          <div className="font-cond uppercase tracking-[0.2em] text-[9px] text-titans-muted">Kickoff</div>
          <div className="font-cond uppercase tracking-wider text-xs">{formatDate(game.date, { month: "short", day: "numeric" })}</div>
        </div>
      </div>
      <div className="flex items-center gap-3 py-2 border-t border-b border-[rgba(15,23,42,0.08)]">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <img src={game.away?.logo} alt={game.away?.abbr} className="w-8 h-8 object-contain shrink-0" />
          <div className="min-w-0">
            <div className="font-display uppercase text-sm truncate">{game.away?.abbr}</div>
          </div>
        </div>
        <div className="font-display uppercase text-titans-muted text-xs">@</div>
        <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
          <div className="min-w-0 text-right">
            <div className="font-display uppercase text-sm truncate">{game.home?.abbr}</div>
          </div>
          <img src={game.home?.logo} alt={game.home?.abbr} className="w-8 h-8 object-contain shrink-0" />
        </div>
      </div>
      {game.venue && (
        <div className="mt-3 text-titans-muted text-xs font-cond uppercase tracking-wider">{game.venue}</div>
      )}
      <Link to="/schedule" className="mt-3 inline-flex items-center gap-1 font-cond uppercase tracking-wider text-xs text-titans-blue hover:text-titans-red">
        Full schedule <ArrowRight size={12} />
      </Link>
    </WidgetShell>
  );
}

function NewsletterWidget() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post(`/newsletter`, { email: email.trim() });
      toast.success("You're on the list.");
      setDone(true); setEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't sign up.");
    } finally { setBusy(false); }
  };

  return (
    <WidgetShell icon={Mail} label="Newsletter" testid="sidebar-newsletter">
      <div className="font-display text-lg uppercase leading-tight mb-1">Get the Titan Standard in your inbox</div>
      <p className="text-titans-muted text-sm font-body mb-3">Weekly. No spam. Every big story.</p>
      <form onSubmit={submit} className="space-y-2">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          data-testid="sidebar-newsletter-email"
          className="w-full bg-white border border-[rgba(15,23,42,0.15)] focus:border-titans-blue focus:outline-none px-3 py-2 text-sm font-body"
        />
        <button
          type="submit"
          disabled={busy || done}
          data-testid="sidebar-newsletter-submit"
          className="w-full bg-titans-blue hover:opacity-90 disabled:opacity-60 text-white px-4 py-2 font-cond uppercase tracking-wider text-xs"
        >
          {done ? "Subscribed ✓" : busy ? "Sending…" : "Subscribe"}
        </button>
      </form>
    </WidgetShell>
  );
}

function TriviaWidget() {
  return (
    <WidgetShell icon={HelpCircle} label="Test Your Titans Fandom" testid="sidebar-trivia" accent="gold">
      <div className="font-display text-lg uppercase leading-tight mb-1">Daily Trivia</div>
      <p className="text-titans-muted text-sm font-body mb-3">
        Prove your Two-Tone Blue knowledge. New question every day.
      </p>
      <Link
        to="/community"
        data-testid="sidebar-trivia-cta"
        className="inline-flex items-center gap-1 bg-titans-red hover:bg-titans-red-hover text-white px-4 py-2 font-cond uppercase tracking-wider text-xs"
      >
        Play now <ArrowRight size={12} />
      </Link>
    </WidgetShell>
  );
}

function CapCentralWidget() {
  return (
    <WidgetShell icon={DollarSign} label="Cap Central" testid="sidebar-cap" accent="blue">
      <div className="font-display text-lg uppercase leading-tight mb-1">Salary Cap Watch</div>
      <p className="text-titans-muted text-sm font-body mb-3">
        Every contract, cap hit, and dead-money hit in one place.
      </p>
      <Link
        to="/community"
        data-testid="sidebar-cap-cta"
        className="inline-flex items-center gap-1 border border-titans-blue text-titans-blue hover:bg-titans-blue hover:text-white px-4 py-2 font-cond uppercase tracking-wider text-xs transition-colors"
      >
        See the board <ArrowRight size={12} />
      </Link>
    </WidgetShell>
  );
}
